import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, CheckCircle, Clock, BarChart2, Loader2, 
  AlertCircle, ChevronRight, GraduationCap, Calendar,
  Trophy, Target, ArrowRight, User
} from 'lucide-react';
import apiClient from '@/utils/apiClient';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';

const AcademicView = () => {
  const { children, selectedChild, setSelectedChild, loading: parentLoading } = useParentData();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('half_yearly'); // 'half_yearly' or 'annual'

  const fetchSyllabus = useCallback(async (studentId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/v1/parent/academic/my-child-syllabus?student_id=${studentId}`);
      setPlan(response.data.plan);
    } catch (error) {
      console.error('Failed to fetch syllabus:', error);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchSyllabus(selectedChild.id);
    }
  }, [selectedChild, fetchSyllabus]);

  useEffect(() => {
    if (plan && plan.subjects) {
      const cats = [];
      plan.subjects.forEach(sub => {
        if (sub.chapters) {
          sub.chapters.forEach(chap => {
            if (chap.exam_category && !cats.includes(chap.exam_category)) {
              cats.push(chap.exam_category);
            }
            if (chap.exam_category_2 && !cats.includes(chap.exam_category_2)) {
              cats.push(chap.exam_category_2);
            }
            if (chap.category_weightages) {
              Object.keys(chap.category_weightages).forEach(c => {
                if (c && !cats.includes(c)) {
                  cats.push(c);
                }
              });
            }
          });
        }
      });
      // Prioritize half_yearly or annual, or select the first category
      const sortedCats = cats.sort((a, b) => {
        if (a === 'half_yearly') return -1;
        if (b === 'half_yearly') return 1;
        if (a === 'annual') return -1;
        if (b === 'annual') return 1;
        return a.localeCompare(b);
      });
      if (sortedCats.length > 0 && !sortedCats.includes(activeTab)) {
        setActiveTab(sortedCats[0]);
      }
    }
  }, [plan, activeTab]);

  if (parentLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">Loading academic progress...</p>
      </div>
    );
  }

  if (!selectedChild) {
    return (
      <div className="p-6">
        <ChildSelector 
          children={children}
          selectedChild={selectedChild}
          onSelectChild={setSelectedChild}
        />
        <div className="bg-white rounded-3xl p-12 border border-dashed border-gray-200 text-center mt-6">
          <User className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">Please select a child</h3>
          <p className="text-gray-400">Select a child from the list above to view their syllabus progress.</p>
        </div>
      </div>
    );
  }
  const getWeightage = (chap, cat) => {
    if (chap.category_weightages && chap.category_weightages[cat] !== undefined) {
      return chap.category_weightages[cat];
    }
    if (chap.exam_category === cat) return chap.weightage;
    if (chap.exam_category_2 === cat) return chap.weightage_2;
    return 0;
  };

  const calculateOverallProgress = () => {
    if (!plan) return 0;
    let totalWeight = 0;
    let doneWeight = 0;
    
    plan.subjects.forEach(sub => {
      sub.chapters.forEach(chap => {
        const weight = getWeightage(chap, activeTab);
        if (weight > 0) {
          totalWeight += weight;
          if (chap.status === 'completed') doneWeight += weight;
        }
      });
    });
    
    if (totalWeight === 0) {
      let totalCount = 0;
      let doneCount = 0;
      plan.subjects.forEach(sub => {
        const chaps = sub.chapters.filter(c => 
          c.exam_category === activeTab || 
          c.exam_category_2 === activeTab ||
          (c.category_weightages && c.category_weightages[activeTab] !== undefined)
        );
        totalCount += chaps.length;
        doneCount += chaps.filter(c => c.status === 'completed').length;
      });
      return totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);
    }
    
    return Math.round((doneWeight / totalWeight) * 100);
  };

  const overallProgress = calculateOverallProgress();

  return (
    <div className="p-6 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Academic Progress</h1>
          <p className="text-gray-600 mt-1">Track your child's syllabus and chapter completion</p>
        </div>
        <ChildSelector 
          children={children}
          selectedChild={selectedChild}
          onSelectChild={setSelectedChild}
        />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Loading syllabus...</p>
        </div>
      ) : !plan ? (
        <div className="bg-white rounded-3xl p-12 border border-dashed border-gray-200 text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No Syllabus Plan Found</h3>
          <p className="text-gray-400">The administration hasn't uploaded the syllabus for {selectedChild.class_name} yet.</p>
        </div>
      ) : (
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Hero Progress Header */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2.5rem] p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <GraduationCap className="w-48 h-48 rotate-12" />
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="space-y-4">
                <span className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-widest border border-white/20">
                  {plan.class_name} Syllabus
                </span>
                <h1 className="text-4xl font-black leading-tight">Academic Journey<br />Progress</h1>
                <p className="text-blue-100 font-medium max-w-sm">Tracking every chapter and milestone for a brighter future.</p>
              </div>

              <div className="flex items-center gap-8">
                <div className="relative w-32 h-32 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64" cy="64" r="56"
                      fill="transparent"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                    />
                    <circle
                      cx="64" cy="64" r="56"
                      fill="transparent"
                      stroke="white"
                      strokeWidth="8"
                      strokeDasharray={351.85}
                      strokeDashoffset={351.85 - (351.85 * overallProgress) / 100}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center">
                    <span className="text-3xl font-black">{overallProgress}%</span>
                    <span className="text-[10px] font-bold uppercase tracking-tighter opacity-70">Syllabus</span>
                  </div>
                </div>
                
                <div className="hidden lg:flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <Trophy className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold opacity-70">Focus Areas</p>
                      <p className="text-sm font-black">{plan.subjects.length} Subjects</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <Target className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold opacity-70">Goal</p>
                      <p className="text-sm font-black">Annual Mastery</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          {(() => {
            const cats = [];
            if (plan && plan.subjects) {
              plan.subjects.forEach(sub => {
                if (sub.chapters) {
                  sub.chapters.forEach(chap => {
                    if (chap.exam_category && !cats.includes(chap.exam_category)) {
                      cats.push(chap.exam_category);
                    }
                    if (chap.exam_category_2 && !cats.includes(chap.exam_category_2)) {
                      cats.push(chap.exam_category_2);
                    }
                    if (chap.category_weightages) {
                      Object.keys(chap.category_weightages).forEach(c => {
                        if (c && !cats.includes(c)) {
                          cats.push(c);
                        }
                      });
                    }
                  });
                }
              });
            }
            const displayCategories = cats.length > 0 ? cats.sort((a, b) => {
              if (a === 'half_yearly') return -1;
              if (b === 'half_yearly') return 1;
              if (a === 'annual') return -1;
              if (b === 'annual') return 1;
              return a.localeCompare(b);
            }) : ['half_yearly', 'annual'];

            return (
              <div className="flex flex-wrap p-1.5 bg-gray-100 rounded-2xl w-fit gap-1">
                {displayCategories.map(cat => {
                  const label = cat === 'half_yearly' ? 'Half-Yearly' : cat === 'annual' ? 'Annual' : (cat === 'general' ? 'General' : cat);
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${
                        activeTab === cat ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label === 'Half-Yearly' || label === 'Annual' || label === 'General' ? `${label} Syllabus` : label}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Subject-wise Chapters Timeline */}
          <div className="space-y-10">
            {plan.subjects.map((sub, sIdx) => {
              const chapters = sub.chapters ? sub.chapters.filter(c => 
                c.exam_category === activeTab || 
                c.exam_category_2 === activeTab ||
                (c.category_weightages && c.category_weightages[activeTab] !== undefined)
              ) : [];

              const completed = chapters.filter(c => c.status === 'completed').length;
              const progress = chapters.length === 0 ? 0 : Math.round((completed / chapters.length) * 100);

              return (
                <div key={sIdx} className="space-y-6">
                  <div className="flex items-end justify-between px-2">
                    <div>
                      <h2 className="text-2xl font-black text-gray-800">{sub.subject_name}</h2>
                      <p className="text-sm text-gray-400 font-medium flex items-center gap-1.5">
                        <GraduationCap className="w-4 h-4" />
                        Teacher: {sub.teacher_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-black text-blue-600">{progress}%</span>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Completed</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {chapters.length === 0 ? (
                      <div className="col-span-full p-8 text-center text-sm text-gray-400 bg-gray-50/50 rounded-[2rem] border-2 border-dashed border-gray-200">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        No chapters scheduled or weightages assigned for this term.
                      </div>
                    ) : (
                      chapters.map((chap, cIdx) => (
                        <div 
                          key={chap.id} 
                          className={`p-5 rounded-[2rem] border-2 transition-all relative overflow-hidden group ${
                            chap.status === 'completed' 
                              ? 'bg-green-50/50 border-green-100' 
                              : 'bg-white border-gray-100 hover:border-blue-100'
                          }`}
                        >
                          <div className="flex items-start justify-between relative z-10">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                {chap.status === 'completed' ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                  <Clock className="w-5 h-5 text-gray-300" />
                                )}
                                <span className={`text-[10px] font-black uppercase tracking-widest ${chap.status === 'completed' ? 'text-green-600' : 'text-gray-400'}`}>
                                  {chap.status === 'completed' ? 'Completed' : 'Upcoming'}
                                </span>
                              </div>
                              <h3 className={`text-lg font-bold leading-tight ${chap.status === 'completed' ? 'text-green-900' : 'text-gray-800'}`}>
                                {chap.name}
                              </h3>
                              <div className="flex items-center gap-4">
                                {chap.timeline && (
                                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {chap.timeline}
                                  </div>
                                )}
                                {getWeightage(chap, activeTab) > 0 && (
                                  <div className="flex items-center gap-1.5 text-xs font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-lg">
                                    <BarChart2 className="w-3.5 h-3.5" />
                                    Weight: {getWeightage(chap, activeTab)}%
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-blue-500 group-hover:text-white transition-all">
                              <ArrowRight className="w-6 h-6" />
                            </div>
                          </div>

                          {chap.status === 'completed' && (
                            <div className="absolute bottom-0 right-0 p-2 opacity-5">
                               <CheckCircle className="w-24 h-24 -mr-8 -mb-8" />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Encouragement Footer */}
          <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white text-center shadow-2xl">
            <h3 className="text-2xl font-black mb-4 italic">"Education is the passport to the future."</h3>
            <p className="text-gray-400 max-w-lg mx-auto">We are committed to providing the best learning environment. Monitor your child's progress regularly to stay aligned with their academic goals.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicView;
